import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Dropzone } from '@/components/ui/dropzone'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { UserLabel } from '@/components/ui/user-label'
import {
  type CheckCsvLeadsResult,
  type CsvIngestionStatusResult,
  type FlaggedCsvLead,
  useCheckCsvLeads,
  useIngestFromCsv,
  useInstantlyCampaigns,
} from '@/models/salesLead'
import { useCreateSalesLeadTag, useSalesLeadTags } from '@/models/salesLeadTag'
import { useTaskStatus } from '@/models/tasks'
import { type StaffUser, staffUserName } from '@/models/user'
import { TagSelect } from './TagSelect'

const AUTO_ASSIGN_VALUE = '__auto__'
const DONT_ASSIGN_VALUE = '__dont_assign__'
const NO_CAMPAIGN_VALUE = '__none__'

interface IngestFromCsvDialogProps {
  staffUsers: StaffUser[]
}

function downloadFlaggedCsv(leads: FlaggedCsvLead[]) {
  const rows: string[][] = [['DOT Number', 'Legal Name', 'Reasons']]
  leads.forEach((l) => rows.push([l.dotNumber, l.legalName, l.flagReasons.join('; ')]))
  const csv = rows.map((r) => r.map((c) => `"${c.replace(/"/g, '""')}"`).join(',')).join('\n')
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }))
  const a = document.createElement('a')
  a.href = url
  a.download = 'flagged_leads.csv'
  a.click()
  URL.revokeObjectURL(url)
}

export function IngestFromCsvDialog({ staffUsers }: IngestFromCsvDialogProps) {
  const [open, setOpen] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [assignedRepId, setAssignedRepId] = useState(AUTO_ASSIGN_VALUE)
  const [campaignSearch, setCampaignSearch] = useState('')
  const [campaignId, setCampaignId] = useState(NO_CAMPAIGN_VALUE)
  const [tagIds, setTagIds] = useState<string[]>([])
  const [checkResult, setCheckResult] = useState<CheckCsvLeadsResult | null>(null)

  const [taskId, setTaskId] = useState<string | null>(null)

  const { data: campaigns = [], isLoading: campaignsLoading } = useInstantlyCampaigns(
    campaignSearch || undefined,
    open
  )
  const { data: allTags = [] } = useSalesLeadTags()
  const createTagMutation = useCreateSalesLeadTag()
  const ingestMutation = useIngestFromCsv()
  const checkMutation = useCheckCsvLeads()
  const { data: ingestionStatus } = useTaskStatus<CsvIngestionStatusResult>(taskId)

  useEffect(() => {
    if (!taskId || !ingestionStatus) return
    const { status, result } = ingestionStatus
    if (status === 'COMPLETED') {
      if (result?.error) {
        toast.error(result.error, { id: taskId })
        setTaskId(null)
        return
      }
      const summary = result ? `${result.created} created, ${result.updated} updated` : 'done'
      toast.success(`CSV ingestion complete — ${summary}`, { id: taskId, duration: 10000 })
      if (result?.rowErrors && result.rowErrors.length > 0) {
        const preview = result.rowErrors
          .slice(0, 5)
          .map((e) => `row ${e.rowNumber} (${e.error})`)
          .join(', ')
        toast.warning(`${result.rowErrors.length} row(s) skipped: ${preview}`, {
          duration: 10000,
        })
      }
      setTaskId(null)
    } else if (status === 'FAILED' || status === 'TIMED_OUT' || status === 'TERMINATED') {
      toast.error('CSV ingestion failed', { id: taskId })
      setTaskId(null)
    } else if (status !== 'RUNNING' && status !== 'UNKNOWN') {
      toast.dismiss(taskId)
      setTaskId(null)
    }
  }, [taskId, ingestionStatus])

  function handleFileChange(newFile: File | null) {
    setFile(newFile)
    setCheckResult(null)
  }

  function toggleTag(tagId: string) {
    setTagIds((prev) => (prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]))
  }

  function createTag(name: string) {
    createTagMutation.mutate(name, {
      onSuccess: (tag) => setTagIds((prev) => (prev.includes(tag.id) ? prev : [...prev, tag.id])),
      onError: () => toast.error('Failed to create tag'),
    })
  }

  function handleCheck() {
    if (!file) return
    checkMutation.mutate(file, {
      onSuccess: (result) => {
        setCheckResult(result)
      },
      onError: () => {
        toast.error('Failed to check leads')
      },
    })
  }

  function handleSubmit() {
    if (!file) {
      toast.error('Select a CSV file to upload')
      return
    }

    ingestMutation.mutate(
      {
        file,
        assignedRepId:
          assignedRepId === AUTO_ASSIGN_VALUE || assignedRepId === DONT_ASSIGN_VALUE
            ? undefined
            : assignedRepId,
        campaignId: campaignId === NO_CAMPAIGN_VALUE ? undefined : campaignId,
        skipAssignment: assignedRepId === DONT_ASSIGN_VALUE,
        tagIds,
      },
      {
        onSuccess: (data) => {
          const tid = data.taskId
          const campaignNote =
            campaignId !== NO_CAMPAIGN_VALUE
              ? ' Leads with emails will be added to the selected campaign.'
              : ''
          toast.loading(`CSV ingestion running…${campaignNote}`, { id: tid, duration: Infinity })
          setTaskId(tid)
          handleOpenChange(false)
        },
        onError: () => {
          toast.error('Failed to start CSV ingestion')
        },
      }
    )
  }

  function handleOpenChange(value: boolean) {
    if (!value) {
      setFile(null)
      setAssignedRepId(AUTO_ASSIGN_VALUE)
      setCampaignSearch('')
      setCampaignId(NO_CAMPAIGN_VALUE)
      setTagIds([])
      setCheckResult(null)
    }
    setOpen(value)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline">Upload CSV</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Upload CSV</DialogTitle>
          <DialogDescription>
            Upload a CSV of leads from any source. We&apos;ll map its columns to our fields automatically and
            create or update sales leads.
          </DialogDescription>
          <div className="text-muted-foreground text-md flex flex-col gap-1.5">
            <p>
              A company/legal name column is required. For the most complete lead records, please also
              include:
            </p>
            <ul className="grid list-disc grid-cols-2 gap-x-4 gap-y-0.5 pl-4 text-sm">
              <li>DOT number</li>
              <li>DBA name</li>
              <li>Contact name</li>
              <li>Email</li>
              <li>Phone</li>
              <li>Cell phone</li>
              <li>Street address</li>
              <li>City</li>
              <li>State</li>
              <li>Zip code</li>
              <li>Power units (fleet size)</li>
              <li>Drivers</li>
              <li>Current insurer</li>
              <li>Policy expiration date</li>
            </ul>
          </div>
        </DialogHeader>

        <div className="flex flex-col gap-5 py-2">
          <Dropzone
            accept=".csv"
            maxSizeMB={10}
            onChange={handleFileChange}
            isLoading={ingestMutation.isPending}
          />

          {checkResult && <CheckLeadsResult result={checkResult} />}

          {/* Assignee */}
          <div className="flex flex-col gap-1.5">
            <Label>Assign to</Label>
            <Select value={assignedRepId} onValueChange={setAssignedRepId}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={AUTO_ASSIGN_VALUE}>Auto-assign (Debbie / Lupita)</SelectItem>
                <SelectItem value={DONT_ASSIGN_VALUE}>Don&apos;t assign (leave unassigned)</SelectItem>
                {staffUsers.map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    <UserLabel name={staffUserName(u)} src={u.avatar} />
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Tags (optional) */}
          <div className="flex flex-col gap-1.5">
            <Label>
              Tags <span className="text-muted-foreground font-normal">(optional)</span>
            </Label>
            <TagSelect
              tags={allTags}
              selectedIds={tagIds}
              onToggle={toggleTag}
              onCreate={createTag}
              placeholder="Add tags"
            />
          </div>

          {/* Campaign (optional) */}
          <div className="flex flex-col gap-1.5">
            <Label>
              Add to Instantly Campaign <span className="text-muted-foreground font-normal">(optional)</span>
            </Label>
            <Input
              placeholder="Search by name…"
              value={campaignSearch}
              onChange={(e) => setCampaignSearch(e.target.value)}
              className="mb-1"
            />
            <Select value={campaignId} onValueChange={setCampaignId} disabled={campaignsLoading}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder={campaignsLoading ? 'Loading…' : 'Select a campaign'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_CAMPAIGN_VALUE}>None</SelectItem>
                {campaigns.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
                {!campaignsLoading && campaigns.length === 0 && (
                  <div className="text-muted-foreground px-3 py-2 text-sm">No campaigns found</div>
                )}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter className="flex-row items-center justify-between gap-2 sm:justify-between">
          <Button
            variant="outline"
            onClick={handleCheck}
            disabled={!file || checkMutation.isPending || ingestMutation.isPending}
            loading={checkMutation.isPending ? 'Checking…' : undefined}
          >
            Check leads
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={!file || ingestMutation.isPending}>
              {ingestMutation.isPending ? 'Starting…' : 'Upload'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

interface CheckLeadsResultProps {
  result: CheckCsvLeadsResult
}

function CheckLeadsResult({ result }: CheckLeadsResultProps) {
  if (!result.dotColumnFound) {
    return (
      <div className="rounded-md bg-yellow-50 px-3 py-2 text-sm text-yellow-800 dark:bg-yellow-950 dark:text-yellow-200">
        No DOT column detected in this CSV — lead status cannot be checked.
      </div>
    )
  }

  if (result.flaggedLeads.length === 0) {
    return (
      <div className="rounded-md bg-green-50 px-3 py-2 text-sm text-green-800 dark:bg-green-950 dark:text-green-200">
        None of the {result.totalInCsv.toLocaleString()} leads in this CSV are assigned, blocked, or closed.
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-950 dark:text-amber-200">
        {result.flaggedLeads.length} of {result.totalInCsv.toLocaleString()} leads should be reviewed before
        uploading.
      </div>
      <div className="overflow-hidden rounded-md border">
        <div className="max-h-48 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted sticky top-0">
              <tr>
                <th className="px-3 py-1.5 text-left font-medium">DOT #</th>
                <th className="px-3 py-1.5 text-left font-medium">Name</th>
                <th className="px-3 py-1.5 text-left font-medium">Reason(s)</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {result.flaggedLeads.map((lead) => (
                <tr key={lead.dotNumber} className="hover:bg-muted/50">
                  <td className="px-3 py-1.5 font-mono text-xs">{lead.dotNumber}</td>
                  <td className="max-w-[160px] truncate px-3 py-1.5">{lead.legalName}</td>
                  <td className="text-muted-foreground px-3 py-1.5">{lead.flagReasons.join(', ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <Button
        type="button"
        variant="link"
        onClick={() => downloadFlaggedCsv(result.flaggedLeads)}
        className="self-start px-0 text-sm"
      >
        ↓ Download flagged leads CSV
      </Button>
    </div>
  )
}
